<template>
  <v-layout>
    <div class="col">
      <v-store-user-info/>
      <payment-breadcrumb :step="1"/>
      <article>
        <header>
          <h1>{{ $t('payment.index-title') }}</h1>
        </header>
        <div class="inner">
          <payment-type-form
            v-if="showPaymentTypeForm"
            :is-payment-type-selected="paymentTypeSelected"
            :is-payment-in-terms="payInTerms"
            :term-agreement="order.approvePaymentInTerms"
            :price="totalProductAmountWithVat"
            @isPaymentInTermsChanged="isPaymentInTermsChanged"
            @termAgreementChanged="termAgreementChanged"
          />
          <v-modal v-if="serviceErrorNotificationVisible"
                   id="errorNotificationModal"
                   @close="closeErrorNotificationModal">
            <h2>&nbsp;</h2>
            <p>{{ $t('generic.service-error') }}</p>
          </v-modal>
        </div>
      </article>
      <article class="fifty-fifty">
        <div v-if="showLoginForm" id="login-form-container" class="col">
          <h3>{{ $t('payment.index-login-title') }}</h3>
          <login-form/>
        </div>
        <div class="col">
          <div v-if="showRegistrationForm" id="registration-form-container">
            <h3 id="registration-form-title">
              {{ isLoggedIn || isRegistered ? $t('payment.index-update-account-title') :
              $t('payment.index-register-title') }}
            </h3>
            <registration-form :must-payment-type-be-selected="showPaymentTypeForm"
                               :is-payment-type-selected="paymentTypeSelected"
                               :pay-in-terms="payInTerms"
                               :order="order"
                               :user="user"/>
            <p>* {{ $t('generic.label-required-fields') }}</p>
          </div>
        </div>
        <div v-if="showStoreUserForm" id="store-user-form-container">
          <store-user-form :order="order"
                           :is-payment-type-selected="paymentTypeSelected"
                           :must-payment-type-be-selected="showPaymentTypeForm" />
        </div>
      </article>
    </div>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import PaymentTypeForm from '../forms/PaymentType'
import {
  SET_CUSTOMER_DETAILS,
  SET_INVOICE_ADDRESS
} from '../store/modules/cart/mutation-types'
import { SET_PREVIOUSLY_LOGGED_IN } from '../store/modules/payment/mutation-types'
import VLayout from '../layouts/PaymentFlow'
import VStoreUserInfo from '../components/user/StoreUserInfo'
import LoginForm from '../forms/LoginForm'
import RegistrationForm from '../forms/RegistrationForm'
import authenticationMixin from '../mixins/authentication'
import bodyClassMixin from '../mixins/body-class'
import PaymentBreadcrumb from '../components/PaymentBreadcrumb'
import { REGISTRATION_STATUS_SUCCESS } from '../store/modules/registration'
import { buildCustomerDetails } from '../types/customer-details'
import { buildAddress } from '../types/address'
import VModal from '../components/modals/Modal'
import StoreUserForm from '../forms/StoreUserCustomerForm'

export default {
  name: 'PaymentPage',
  components: {
    VModal,
    VStoreUserInfo,
    PaymentTypeForm,
    PaymentBreadcrumb,
    VLayout,
    RegistrationForm,
    LoginForm,
    StoreUserForm
  },
  mixins: [authenticationMixin, bodyClassMixin],
  data: function () {
    return {
      serviceError: null,
      serviceErrorNotificationVisible: false
    }
  },
  computed: {
    ...mapGetters({
      payInTerms: 'cart/payInTerms',
      paymentConfigurations: 'payment/paymentConfigurations',
      totalProductAmountWithVat: 'cart/totalAmountWithVat'
    }),
    ...mapState({
      user: state => state.user.user,
      previouslyLoggedIn: state => state.payment.previouslyLoggedIn,
      isRegistered: state => state.registration.status === REGISTRATION_STATUS_SUCCESS,
      order: state => state.cart.order,
      priceSpecification: state => state.cart.priceSpecification,
      paymentTypeSelected: state => state.cart.paymentTypeSelected
    }),
    userProfile: function () {
      return this.user && this.user.profile ? this.user.profile : null
    },
    showLoginForm: function () {
      return (!this.payInTermsAvailable || this.paymentTypeSelected) && !this.isLoggedIn && !this.isRegistered
    },
    showPaymentTypeForm: function () {
      if (!this.order || !this.priceSpecification) {
        return false
      }
      return this.order && this.priceSpecification && (this.isStoreUser || this.payInTermsAvailable)
    },
    showRegistrationForm: function () {
      return (!this.payInTermsAvailable || this.paymentTypeSelected || this.isLoggedIn) && (!this.isStoreUser)
    },
    showStoreUserForm: function () {
      return this.paymentTypeSelected && this.isStoreUser
    },
    payInTermsAvailable: function () {
      for (let paymentConfigurationKey in this.paymentConfigurations) {
        let paymentConfiguration = this.paymentConfigurations[paymentConfigurationKey]
        if (paymentConfiguration.paymentTermConfigurations && paymentConfiguration.paymentTermConfigurations.length > 0) {
          return true
        }
      }
      return false
    }
  },
  watch: {
    userProfile: function (newProfile) {
      if (newProfile !== null) {
        this.updateOrderFromUser()
      }
    }
  },
  mounted: async function () {
    try {
      await Promise.all([
        this.$store.dispatch('payment/updatePaymentMethods'),
        this.$store.dispatch('user/tryFetchUser')
      ])
    } catch (e) {
      this.serviceErrorNotificationVisible = true
      this.serviceError = e
    }
    this.updateOrderFromUser()
  },
  methods: {
    updateOrderFromUser () {
      if (this.userProfile) {
        const customerDetails = { ...this.order.customerDetails, ...this.userProfile }
        delete customerDetails.address
        this.$store.commit('cart/' + SET_CUSTOMER_DETAILS, customerDetails)
        const invoiceAddress = { ...this.order.invoiceAddress, ...this.userProfile.address }
        this.$store.commit('cart/' + SET_INVOICE_ADDRESS, invoiceAddress)
      } else if (this.previouslyLoggedIn && !this.isStoreUser) {
        // Reset customer details when user was logged out before
        this.$store.commit('cart/' + SET_CUSTOMER_DETAILS, buildCustomerDetails())
        this.$store.commit('cart/' + SET_INVOICE_ADDRESS, buildAddress())
      }
      this.$store.commit('payment/' + SET_PREVIOUSLY_LOGGED_IN, this.user !== null)
    },
    isPaymentInTermsChanged (payInTerms) {
      this.$store.dispatch('cart/' + 'switchPayInTerms', payInTerms)
    },
    termAgreementChanged (agreeToTermPayments) {
      this.$store.dispatch('cart/' + 'switchTermAgreement', agreeToTermPayments)
    },
    closeErrorNotificationModal () {
      this.serviceErrorNotificationVisible = false
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/Payment.vue